import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../AnimatedSection';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import {
  REFERRAL_STATUS_LABELS,
  REFERRAL_STATUS_TONES,
} from '../../constants/partnerNetwork';
import { PartnerDashboard as Dashboard, getDashboard } from '../../services/partnerPortal';
import { formatDate, formatMoney, formatRate } from '../../utils/partnerFormat';
import {
  Alert,
  EmptyState,
  Spinner,
  StatCard,
  StatusBadge,
} from '../partner-network/ui';

const PartnerDashboard: React.FC = () => {
  const { partner } = usePartnerAuth();

  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    getDashboard()
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load your dashboard');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner className="h-10 w-10" />
      </div>
    );
  }

  return (
    <section className="py-8 md:py-12">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="slide-up">
          <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="mb-2 font-display text-2xl font-semibold text-white md:text-3xl">
                Welcome back, {partner?.fullName.split(' ')[0]}
              </h1>
              <p className="text-sm text-muted">
                Your Partner ID is{' '}
                <span className="font-mono text-primary-400">{partner?.partnerId}</span> ·
                commission rate {formatRate(partner?.commissionRate)}
              </p>
            </div>
            <Link to="/partner/referrals/new" className="btn-primary py-2.5 px-5">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Submit referral
            </Link>
          </div>
        </AnimatedSection>

        {error && (
          <Alert tone="error" className="mb-6">
            {error}
          </Alert>
        )}

        {data && (
          <>
            <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3">
              <StatCard
                label="Total referrals"
                value={data.referrals.total}
                hint="Everything you have submitted"
              />
              <StatCard
                label="Active"
                value={data.referrals.active}
                hint="Currently being worked on"
              />
              <StatCard
                label="Won"
                value={data.referrals.won}
                hint="Closed successfully"
                tone="success"
              />
            </div>

            <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <StatCard
                label="Pending commission"
                value={formatMoney(data.commissions.pending, data.commissions.currency)}
                hint="Earned, not yet paid"
                tone="warning"
              />
              <StatCard
                label="Paid commission"
                value={formatMoney(data.commissions.paid, data.commissions.currency)}
                hint="Already in your account"
                tone="success"
              />
              <StatCard
                label="Total earned"
                value={formatMoney(data.commissions.totalEarned, data.commissions.currency)}
                hint="Across every referral"
              />
            </div>

            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <div className="mb-5 flex items-center justify-between gap-4">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted">
                  Recent referrals
                </h2>
                <Link
                  to="/partner/referrals"
                  className="text-sm text-primary-400 hover:underline"
                >
                  See all
                </Link>
              </div>

              {data.recentReferrals.length === 0 ? (
                <EmptyState
                  title="No referrals yet"
                  description="When you know a hospitality or commerce business that needs what we build, submit it here and we take it from there."
                  action={
                    <Link to="/partner/referrals/new" className="btn-primary py-2.5 px-5">
                      Submit your first referral
                    </Link>
                  }
                />
              ) : (
                <ul className="space-y-3">
                  {data.recentReferrals.map((referral) => (
                    <li key={referral.id}>
                      <Link
                        to={`/partner/referrals/${referral.id}`}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-surface-border bg-surface px-4 py-3 transition-colors hover:border-primary-500/30"
                      >
                        <span className="min-w-0">
                          <span className="block font-medium text-white">
                            {referral.businessName}
                          </span>
                          <span className="block font-mono text-xs text-muted">
                            {referral.referralId} · {formatDate(referral.submittedAt)}
                          </span>
                        </span>
                        <span className="flex items-center gap-3">
                          {referral.potentialCommission !== null && (
                            <span className="text-sm text-muted">
                              {formatMoney(referral.potentialCommission)}
                            </span>
                          )}
                          <StatusBadge
                            label={REFERRAL_STATUS_LABELS[referral.status]}
                            tone={REFERRAL_STATUS_TONES[referral.status]}
                          />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default PartnerDashboard;
